import { Download, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { DocumentItem, KnowledgeBase } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { LoadingState } from "@/components/feedback/LoadingState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DocumentStatusBadge } from "@/features/knowledge-bases/components/DocumentStatusBadge";
import { DocumentUploader } from "@/features/knowledge-bases/components/DocumentUploader";
import {
  downloadDocument,
  useDeleteDocument,
  useKbDocuments,
  useReindexDocument,
  useUpdateDocument,
} from "@/features/knowledge-bases/hooks/useDocuments";
import { formatBytes } from "@/lib/upload";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  kb: KnowledgeBase;
}

export function DocumentTab({ kb }: Props) {
  const { t } = useTranslation("knowledgeBases");
  const { t: tc } = useTranslation("common");
  const { data, isLoading } = useKbDocuments(kb.id);
  const remove = useDeleteDocument(kb.id);
  const reindex = useReindexDocument(kb.id);
  const updateDoc = useUpdateDocument(kb.id);
  const canUpload = useCan(Action.DOCUMENT_UPLOADED, {
    owner_team_id: kb.owner_team_id,
  });
  const canReindex = useCan(Action.DOCUMENT_REINDEXED, {
    owner_team_id: kb.owner_team_id,
  });

  const [confirmReindex, setConfirmReindex] = useState<DocumentItem | null>(
    null,
  );
  const [editDoc, setEditDoc] = useState<DocumentItem | null>(null);
  const [editValue, setEditValue] = useState("");

  function openEdit(doc: DocumentItem) {
    setEditDoc(doc);
    setEditValue(doc.description ?? "");
  }

  async function handleDownload(doc: DocumentItem) {
    try {
      await downloadDocument(doc.id, doc.filename);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleSaveDescription() {
    if (!editDoc) return;
    try {
      await updateDoc.mutateAsync({
        documentId: editDoc.id,
        patch: { description: editValue.trim() || null },
      });
      toast.success(t("document.toasts.descriptionSaved"));
      setEditDoc(null);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete(doc: DocumentItem) {
    try {
      await remove.mutateAsync(doc.id);
      toast.success(t("document.toasts.removed"));
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleReindex(doc: DocumentItem) {
    try {
      await reindex.mutateAsync(doc.id);
      toast.success(t("document.toasts.reindexQueued"));
    } catch (err) {
      toastApiError(err);
    } finally {
      setConfirmReindex(null);
    }
  }

  return (
    <div className="space-y-4 py-4">
      {canUpload ? <DocumentUploader kbId={kb.id} /> : null}

      <p className="text-xs text-muted-foreground">
        {t("document.indexingNote")}
      </p>

      {isLoading ? <LoadingState /> : null}

      {data && data.length === 0 && !isLoading ? (
        <EmptyState
          title={t("document.empty.title")}
          description={t("document.empty.description")}
        />
      ) : null}

      {data && data.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.name")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.description")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.size")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.status")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.chunks")}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("document.columns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((doc) => (
                <tr key={doc.id} className="border-t">
                  <td className="px-3 py-2">{doc.filename}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <span className="max-w-[16rem] truncate text-muted-foreground">
                        {doc.description ?? t("document.noDescription")}
                      </span>
                      {canUpload ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 shrink-0"
                          onClick={() => openEdit(doc)}
                          aria-label={t("document.aria.editDescription")}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {formatBytes(doc.size_bytes)}
                  </td>
                  <td className="px-3 py-2">
                    <DocumentStatusBadge
                      status={doc.status}
                      errorMessage={doc.error_message}
                    />
                  </td>
                  <td className="px-3 py-2">{doc.chunk_count ?? "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => void handleDownload(doc)}
                        aria-label={t("document.aria.download")}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {canReindex ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmReindex(doc)}
                          disabled={
                            reindex.isPending ||
                            doc.status === "processing"
                          }
                          aria-label={t("document.aria.reindex")}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {canUpload ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(doc)}
                          disabled={remove.isPending}
                          aria-label={t("document.aria.remove")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <AlertDialog
        open={Boolean(confirmReindex)}
        onOpenChange={(v) => !v && setConfirmReindex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("document.reindexConfirm.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("document.reindexConfirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmReindex && handleReindex(confirmReindex)
              }
            >
              {t("document.reindexConfirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(editDoc)}
        onOpenChange={(v) => !v && setEditDoc(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("document.editDescription.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="doc-desc">
              {t("document.editDescription.label")}
            </Label>
            <Textarea
              id="doc-desc"
              rows={3}
              maxLength={2000}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={t("document.editDescription.placeholder")}
            />
            <p className="text-xs text-muted-foreground">
              {t("document.editDescription.hint")}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditDoc(null)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSaveDescription}
              disabled={updateDoc.isPending}
            >
              {t("document.editDescription.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
